/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { isArray } from 'lodash';
import {
    Box,
    Typography,
    TextField,
    Autocomplete,
    Chip,
    CircularProgress,
    alpha,
    IconButton,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Checkbox,
    Divider,
    Badge,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
    Switch,
    FormControlLabel,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Iconify } from 'src/components';
import { useSelector } from 'react-redux';
import { fetchTreatmentStatusSummary, fetchTreatmentStatusSummaryCohort, setTreatmentStatusSummary } from 'src/redux/slices/analyticsReports';
import { dispatch, useSelector as useReduxSelector } from 'src/redux/store';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
    setTablePreferenceLoading,
} from 'src/redux/slices/tablePreferences';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import { useNavigate } from 'react-router-dom';
import { PATH_DASHBOARD } from 'src/routes/paths';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import TreatmentLeadPopup from './TreatmentLeadPopup';
import DataScopeIndicator from './DataScopeIndicator';
import MetricLineChart from './MetricLineChart';
import { reportDayStart, reportDayEnd } from '../reportMetricSpecs';

// Status palette — lead-stage colour system shared with chips / KPI cards / funnel bars.
const STATUS_COLUMNS = [
    { id: 'NEW', label: 'Enquiries', color: '#3b82f6' },
    { id: 'CONSULTATION_BOOKED', label: 'Consult Booked', color: '#8b5cf6' },
    { id: 'CONSULTATION_ATTENDED', label: 'Consult Attended', color: '#f59e0b' },
    { id: 'TREATMENT_STARTED', label: 'Treatment Started', color: '#10b981' },
    { id: 'CLOSED', label: 'Completed Treatment', color: '#64748b' },
];

const TABLE_NAME = 'Treatment Status Summary Table';

const ALL_COLUMNS = [
    { field: 'treatmentName', label: 'Treatment' },
    { field: 'NEW', label: 'Enquiries' },
    { field: 'CONSULTATION_BOOKED', label: 'Consult Booked' },
    { field: 'CONSULTATION_ATTENDED', label: 'Consult Attended' },
    { field: 'TREATMENT_STARTED', label: 'Treatment Started' },
    { field: 'CLOSED', label: 'Completed Treatment' },
    { field: 'total', label: 'Total' },
];

const TOOLTIP_TEXT = {
    activity: 'Enquiries = distinct leads created in the selected date range (matches the treatment chips above). The other columns are Activity in period: all leads that moved into each status during the selected date range, regardless of when they first enquired. For example, if a patient enquired last month but had their consultation booked this month, they will appear under Consult Booked. A lead can appear in multiple columns. Click any number to view the leads.',
    cohort: 'Shows only leads that enquired during the selected date range, and tracks where each one currently sits in the pipeline. For example, selecting "Last 7 Days" shows all new enquiries from the past week and how many have progressed to Booked, Attended, etc. Click any number to view the leads.',
};

export default function TreatmentStatusSummaryTable({
    practiceId,
    practiceIds,
    dateRange,
    treatments,
    filterTreatmentIds,
}) {
    const navigate = useNavigate();
    const buildExportName = useReportExportFilename();
    const [selectedTreatments, setSelectedTreatments] = useState([]);
    const [viewMode, setViewMode] = useState('activity');
    const [view, setView] = useState('table');
    const [sortModel, setSortModel] = useState([{ field: 'NEW', sort: 'desc' }]);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupData, setPopupData] = useState({
        status: '',
        treatmentId: null,
        treatmentName: '',
    });
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [exportAnchor, setExportAnchor] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [prefsLoaded, setPrefsLoaded] = useState(false);

    const { treatmentStatusSummary } = useSelector((state) => state.analyticsReports || {});
    const summaryData = treatmentStatusSummary?.data || [];
    const isLoading = treatmentStatusSummary?.isLoading || false;

    // ── NHS treatment visibility (FE-only) ───────────────────────────────────
    // Hide NHS-categorised treatment rows by default, with a toggle to show them.
    // The practice flag (is_accepting_nhs_patients) is the INTENDED signal for
    // "this practice offers NHS", but it is NOT reliably loaded into Redux on this
    // page, so we ALSO surface the toggle whenever the data actually contains NHS
    // rows — which is exactly when hiding is useful. The flag is NOT the row
    // classifier; rows are classified from treatment metadata (see isNhsTreatment).
    const isAcceptingNhs = useSelector(
        (state) => state.practice?.practice?.preferences?.is_accepting_nhs_patients
    ) || false;
    const [showNhs, setShowNhs] = useState(false); // default: NHS rows hidden

    // Build an id→treatment map for classification (treatments = practiceTreatments).
    const treatmentsById = useMemo(() => {
        const map = {};
        (treatments || []).forEach((t) => { if (t?.id != null) map[t.id] = t; });
        return map;
    }, [treatments]);

    // Classify a summary row as NHS.
    // NOTE: today this report's fields= does NOT return a structured NHS flag, so
    // classification is effectively the category/name convention below. The
    // is_nhs / is_nhs_category branches are forward-compat: they activate only
    // if/when the backend exposes a treatment-level NHS flag on this data.
    // `looksNhs` matches "NHS" as a whole word but excludes "non-NHS"/"non nhs"
    // so we never hide a PRIVATE row by accident (default is hidden, so a false
    // positive is costly). Category is the more stable signal than the free-text
    // treatment name, but we check both (name covers treatmentsById misses).
    const looksNhs = useCallback((s) => {
        if (!s) return false;
        const v = String(s).toLowerCase();
        if (/non[-\s]?nhs/.test(v)) return false;
        return /\bnhs\b/.test(v);
    }, []);
    const isNhsTreatment = useCallback((row) => {
        const t = treatmentsById[row.treatmentId];
        if (t?.is_nhs != null) return !!t.is_nhs;
        if (t?.treatment_item?.is_nhs_category != null) return !!t.treatment_item.is_nhs_category;
        return looksNhs(t?.treatment_item?.treatment_category?.name)
            || looksNhs(t?.treatment_item?.name)
            || looksNhs(row.treatmentName);
    }, [treatmentsById, looksNhs]);

    // Show the toggle when the practice offers NHS OR the data has NHS rows to hide.
    const hasNhsRows = useMemo(
        () => (summaryData || []).some((row) => isNhsTreatment(row)),
        [summaryData, isNhsTreatment]
    );
    const showNhsToggle = isAcceptingNhs || hasNhsRows;

    // Table preferences from Redux
    const userId = useGetCurrentUserId();
    const { tablePreferences: tablePrefsRaw, globalTablePreferences } = useReduxSelector((state) => state.tablePreferences);
    const prefResults = tablePrefsRaw?.results || tablePrefsRaw || [];

    // Load user preferences on mount
    useEffect(() => {
        if (userId) {
            dispatch(getTablePreferences({ practiceUserId: userId }));
        }
    }, [userId]);

    // Save preferences to API
    const savePreferences = useCallback((newHidden) => {
        if (!userId) return;
        dispatch(setTablePreferenceLoading(true));
        const tData = getCurrentTableData(prefResults, TABLE_NAME);
        const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
        const currentTableData = isArray(tData) && tData.length > 0 ? tData : globalData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const updatedPrefs = { ...currentPreferences, hiddenColumns: newHidden };
        const payload = {
            body: { table_name: TABLE_NAME, preferences: updatedPrefs },
            practiceUserId: userId,
        };
        if (isArray(tData) && tData.length > 0) {
            dispatch(handleTablePreferencesEdit({ ...payload, tableId: tData[0].id }));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    }, [userId, prefResults, globalTablePreferences]);

    // Load saved preferences
    useEffect(() => {
        if (!prefsLoaded && userId) {
            const tData = getCurrentTableData(prefResults, TABLE_NAME);
            const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
            const currentTableData = isArray(tData) && tData.length > 0 ? tData : globalData;
            const prefs = currentTableData?.[0]?.preferences;
            if (prefs?.hiddenColumns && isArray(prefs.hiddenColumns)) {
                setHiddenColumns(prefs.hiddenColumns);
            }
            setPrefsLoaded(true);
        }
    }, [prefResults, globalTablePreferences, userId]);

    // Column visibility model for DataGrid
    const columnVisibilityModel = useMemo(() => {
        const model = {};
        hiddenColumns.forEach((field) => { model[field] = false; });
        return model;
    }, [hiddenColumns]);

    const isFilterActive = hiddenColumns.length > 0;

    const handleToggleColumn = useCallback((field) => {
        const updated = hiddenColumns.includes(field)
            ? hiddenColumns.filter((f) => f !== field)
            : [...hiddenColumns, field];
        setHiddenColumns(updated);
        savePreferences(updated);
    }, [hiddenColumns, savePreferences]);

    const practiceIdsKey = (practiceIds || []).join(',');

    // Fetch data when practiceId, dateRange, or viewMode changes
    const fetchData = useCallback(() => {
        if (!practiceId || !treatments?.length) {
            return;
        }

        const formattedDateRange = dateRange ? {
            start: reportDayStart(dateRange.startDate),
            end: reportDayEnd(dateRange.endDate),
        } : null;

        if (viewMode === 'cohort') {
            dispatch(fetchTreatmentStatusSummaryCohort(practiceId, treatments, formattedDateRange));
        } else {
            // Same practice scope as the cards/funnel (practice__in when >1).
            dispatch(fetchTreatmentStatusSummary(practiceIds?.length ? practiceIds : practiceId, treatments, formattedDateRange));
        }
    }, [practiceId, practiceIdsKey, treatments, dateRange, viewMode]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Cleanup on unmount
    useEffect(() => () => {
        dispatch(setTreatmentStatusSummary({ data: [], isLoading: false }));
    }, []);

    // Clear multi-select when category filter changes
    useEffect(() => {
        setSelectedTreatments([]);
    }, [filterTreatmentIds]);

    // Apply category filter first (from parent page tabs), then user's multi-select filter
    // Base rows after the category filter (parent tabs) + NHS visibility filter,
    // BEFORE the user's multi-select. Also the source for the treatment filter
    // dropdown, so it never offers a treatment the NHS filter would then hide
    // (which would yield an empty table). API data is untouched, so
    // totals/drilldowns/scheduled reports are unaffected.
    const baseData = useMemo(() => {
        let data = summaryData;
        if (filterTreatmentIds) {
            data = data.filter((row) => filterTreatmentIds.has(row.treatmentId));
        }
        if (!showNhs) {
            data = data.filter((row) => !isNhsTreatment(row));
        }
        return data;
    }, [summaryData, filterTreatmentIds, showNhs, isNhsTreatment]);

    // Table/graph/export data — baseData narrowed by the user's multi-select.
    const filteredData = useMemo(() => {
        if (selectedTreatments?.length > 0) {
            const selectedIds = selectedTreatments.map((t) => t.treatmentId);
            return baseData.filter((row) => selectedIds.includes(row.treatmentId));
        }
        return baseData;
    }, [baseData, selectedTreatments]);

    // If NHS is toggled off while NHS treatments are selected, drop them from the
    // selection so the table doesn't go empty (stale chips referencing hidden rows).
    useEffect(() => {
        if (!showNhs && selectedTreatments.some((t) => isNhsTreatment(t))) {
            setSelectedTreatments((prev) => prev.filter((t) => !isNhsTreatment(t)));
        }
    }, [showNhs, isNhsTreatment, selectedTreatments]);

    // Derived data for graph view — built from the same filteredData as the table
    const chartCategories = useMemo(
        () => filteredData.map((row) => row.treatmentName || ''),
        [filteredData]
    );

    const chartSeries = useMemo(
        () => STATUS_COLUMNS
            .filter((status) => !hiddenColumns.includes(status.id))
            .map((status) => ({
                name: status.label,
                data: filteredData.map((row) => row[status.id] || 0),
                color: status.color,
            })),
        [filteredData, hiddenColumns]
    );

    // Export CSV — exports exactly what the user currently sees (filtered view)
    const handleExportCSV = useCallback(() => {
        setExportAnchor(null);
        if (!filteredData || filteredData.length === 0) return;
        // Activity view: the trailing total duplicates the Enquiries (NEW) column, so it is not exported.
        const visibleCols = ALL_COLUMNS.filter((c) => !hiddenColumns.includes(c.field) && (viewMode === 'cohort' || c.field !== 'total'));
        // The 'total' column's on-screen header is dynamic (Cohort Size vs
        // Enquiries by viewMode); mirror it so the CSV header matches the grid.
        const totalLabel = viewMode === 'cohort' ? 'Cohort Size' : 'Enquiries';
        const headers = visibleCols.map((c) => (c.field === 'total' ? totalLabel : c.label));
        const rows = filteredData.map((row) => visibleCols.map((col) => {
            if (col.field === 'treatmentName') return row.treatmentName || '-';
            if (col.field === 'total') return row.total || 0;
            return row[col.field] || 0;
        }));
        const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const modeLabel = viewMode === 'cohort' ? 'cohort' : 'activity';
        link.download = buildExportName(`treatment-status-summary-${modeLabel}`, dateRange);
        link.click();
        URL.revokeObjectURL(link.href);
    }, [filteredData, hiddenColumns, viewMode, dateRange, buildExportName]);

    // Handle cell click for popup
    const handleCellClick = (treatment, status) => {
        setPopupData({
            status: status.id,
            statusLabel: status.label,
            treatmentId: treatment.treatmentId,
            treatmentItemId: treatment.treatmentItemId,
            treatmentName: treatment.treatmentName,
        });
        setPopupOpen(true);
    };

    const handlePopupClose = () => {
        setPopupOpen(false);
    };

    // Build columns
    const columns = useMemo(() => {
        const cols = [
            {
                field: 'treatmentName',
                headerName: 'Treatment',
                flex: 1.5,
                renderCell: (params) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 1 }}>
                        <Box
                            sx={{
                                width: 4,
                                height: 32,
                                borderRadius: 1,
                                bgcolor: params.row.color || '#6366f1',
                            }}
                        />
                        <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                            {params.value}
                        </Typography>
                    </Box>
                ),
            },
        ];

        // Add status columns
        STATUS_COLUMNS.forEach((status) => {
            cols.push({
                field: status.id,
                headerName: status.label,
                flex: 1,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => {
                    const value = params.value || 0;
                    return (
                        <Chip
                            label={value}
                            size="small"
                            onClick={() => value > 0 && handleCellClick(params.row, status)}
                            sx={{
                                bgcolor: value > 0 ? alpha(status.color, 0.12) : 'transparent',
                                color: value > 0 ? status.color : 'text.disabled',
                                fontWeight: 600,
                                fontSize: '12px',
                                height: 26,
                                minWidth: 40,
                                cursor: value > 0 ? 'pointer' : 'default',
                                '&:hover': value > 0 ? {
                                    bgcolor: alpha(status.color, 0.2),
                                } : {},
                            }}
                        />
                    );
                },
            });
        });

        // Add total column — label changes based on view mode. Click drills into
        // NEW (the underlying lead universe), so the popup count matches the cell.
        // Activity view: total === the Enquiries (NEW) column already shown, so only
        // render the trailing total in cohort view ("Cohort Size").
        if (viewMode === 'cohort') cols.push({
            field: 'total',
            headerName: viewMode === 'cohort' ? 'Cohort Size' : 'Enquiries',
            flex: 0.8,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const totalLabel = viewMode === 'cohort' ? 'Cohort' : 'Enquiries';
                return (
                    <Box
                        onClick={value > 0 ? () => handleCellClick(params.row, { id: 'NEW', label: totalLabel }) : undefined}
                        sx={{
                            pr: 2,
                            cursor: value > 0 ? 'pointer' : 'default',
                            '&:hover': value > 0 ? { opacity: 0.7 } : {},
                        }}
                    >
                        <Typography sx={{ fontWeight: 700, fontSize: '14px', color: value > 0 ? '#1e293b' : 'text.disabled' }}>
                            {value}
                        </Typography>
                    </Box>
                );
            },
        });

        return cols;
    }, [viewMode]);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                mt: 3,
            }}
        >
            {/* Header with Toggle + Multi-Select + Actions */}
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                    Treatment Status Summary
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {/* View mode toggle with info icons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, val) => { if (val) setViewMode(val); }}
                            size="small"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    textTransform: 'none',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    px: 1.5,
                                    py: 0.5,
                                    borderColor: '#e2e8f0',
                                    '&.Mui-selected': {
                                        bgcolor: alpha('#3b82f6', 0.08),
                                        color: '#3b82f6',
                                        borderColor: '#3b82f6',
                                        '&:hover': { bgcolor: alpha('#3b82f6', 0.12) },
                                    },
                                },
                            }}
                        >
                            <ToggleButton value="activity">
                                <Iconify icon="mdi:chart-bar" width={14} sx={{ mr: 0.5 }} />
                                All Activity
                            </ToggleButton>
                            <ToggleButton value="cohort">
                                <Iconify icon="mdi:filter-variant" width={14} sx={{ mr: 0.5 }} />
                                Enquiry Cohort
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Tooltip
                            title={TOOLTIP_TEXT[viewMode]}
                            placement="bottom"
                            arrow
                            slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 360, lineHeight: 1.5, p: 1.5 } } }}
                        >
                            <IconButton size="small" sx={{ width: 26, height: 26, color: '#94a3b8', '&:hover': { color: '#64748b', bgcolor: alpha('#3b82f6', 0.08) } }}>
                                <Iconify icon="mdi:eye-outline" width={16} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Table / Graph view toggle */}
                    <ToggleButtonGroup
                        value={view}
                        exclusive
                        onChange={(e, val) => { if (val) setView(val); }}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                textTransform: 'none',
                                fontSize: '12px',
                                fontWeight: 500,
                                px: 1.5,
                                py: 0.5,
                                borderColor: '#e2e8f0',
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    borderColor: 'primary.main',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                },
                            },
                        }}
                    >
                        <ToggleButton value="table">
                            <Iconify icon="mdi:table" width={14} sx={{ mr: 0.5 }} />
                            Table
                        </ToggleButton>
                        <ToggleButton value="graph">
                            <Iconify icon="mdi:chart-bar" width={14} sx={{ mr: 0.5 }} />
                            Graph
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    {/* Multi-select treatment filter */}
                    <Autocomplete
                        multiple
                        size="small"
                        options={baseData}
                        getOptionLabel={(option) => option.treatmentName || ''}
                        value={selectedTreatments}
                        onChange={(e, newVal) => setSelectedTreatments(newVal)}
                        isOptionEqualToValue={(option, value) => option.treatmentId === value.treatmentId}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={selectedTreatments.length === 0 ? 'Filter treatments...' : ''}
                                sx={{ minWidth: 220 }}
                            />
                        )}
                        renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option.treatmentId}
                                    label={option.treatmentName}
                                    size="small"
                                    sx={{
                                        height: 22,
                                        fontSize: '11px',
                                        bgcolor: alpha(option.color || '#6366f1', 0.12),
                                        color: option.color || '#6366f1',
                                        fontWeight: 600,
                                        '& .MuiChip-deleteIcon': {
                                            fontSize: '14px',
                                            color: alpha(option.color || '#6366f1', 0.5),
                                            '&:hover': { color: option.color || '#6366f1' },
                                        },
                                    }}
                                />
                            ))
                        }
                        renderOption={(props, option) => (
                            <li {...props} key={option.treatmentId}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: option.color || '#6366f1' }} />
                                    <Typography sx={{ fontSize: '13px' }}>{option.treatmentName}</Typography>
                                </Box>
                            </li>
                        )}
                        sx={{ '& .MuiOutlinedInput-root': { py: '2px' } }}
                    />

                    {/* Show NHS treatments — shown when the practice offers NHS or the data has NHS rows; default off (NHS hidden) */}
                    {showNhsToggle && (
                        <>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                            <Tooltip title="NHS treatment categories are hidden by default. Toggle on to include them in the table, graph and export.">
                                <FormControlLabel
                                    control={<Switch size="small" checked={showNhs} onChange={(e) => setShowNhs(e.target.checked)} />}
                                    label="Show NHS"
                                    sx={{ ml: 0.25, mr: 0, '& .MuiFormControlLabel-label': { fontSize: '13px', whiteSpace: 'nowrap' } }}
                                />
                            </Tooltip>
                        </>
                    )}

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    {/* Column visibility */}
                    <Tooltip title="Show/hide columns" arrow>
                        <Badge
                            variant="dot"
                            invisible={!isFilterActive}
                            sx={{
                                '& .MuiBadge-badge': {
                                    bgcolor: '#3b82f6',
                                    width: 8,
                                    height: 8,
                                    minWidth: 8,
                                    top: 4,
                                    right: 4,
                                },
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => setColumnsAnchor(e.currentTarget)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: (isFilterActive || columnsAnchor) ? 'primary.main' : '#e2e8f0',
                                    borderRadius: '8px',
                                    bgcolor: columnsAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:options-2-outline" width={16} sx={{ color: (isFilterActive || columnsAnchor) ? 'primary.main' : '#64748b' }} />
                            </IconButton>
                        </Badge>
                    </Tooltip>

                    {/* Export */}
                    <Tooltip title="Export data" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => setExportAnchor(e.currentTarget)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: exportAnchor ? 'primary.main' : '#e2e8f0',
                                borderRadius: '8px',
                                bgcolor: exportAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                            }}
                        >
                            <Iconify icon="eva:download-outline" width={16} sx={{ color: exportAnchor ? 'primary.main' : '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Schedule email report */}
                    <Tooltip title="Schedule this report via email" arrow>
                        <IconButton
                            size="small"
                            onClick={() => navigate(`${PATH_DASHBOARD.analyticsReports.settings}?prefill=TREATMENT_STATUS_COUNTS`)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: '#e2e8f0',
                                borderRadius: '8px',
                                '&:hover': { bgcolor: alpha('#7c3aed', 0.08), borderColor: '#7c3aed' },
                            }}
                        >
                            <Iconify icon="mdi:email-fast-outline" width={16} sx={{ color: '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Column visibility popover */}
                    <Popover
                        open={Boolean(columnsAnchor)}
                        anchorEl={columnsAnchor}
                        onClose={() => setColumnsAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', width: 240, maxHeight: 400 } } }}
                    >
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                Columns
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0.5 }}>
                            {ALL_COLUMNS.filter((c) => viewMode === 'cohort' || c.field !== 'total').map((col) => {
                                const isVisible = !hiddenColumns.includes(col.field);
                                return (
                                    <ListItemButton
                                        key={col.field}
                                        onClick={() => handleToggleColumn(col.field)}
                                        sx={{ py: 0.25, px: 1.5, opacity: isVisible ? 1 : 0.6 }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={isVisible}
                                            sx={{ p: 0, mr: 1 }}
                                        />
                                        <ListItemText
                                            primary={col.label}
                                            primaryTypographyProps={{ fontSize: '13px', color: isVisible ? '#1e293b' : '#94a3b8' }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Popover>

                    {/* Export popover */}
                    <Popover
                        open={Boolean(exportAnchor)}
                        anchorEl={exportAnchor}
                        onClose={() => setExportAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 200 } } }}
                    >
                        <List dense sx={{ py: 0.5 }}>
                            <ListItemButton onClick={handleExportCSV} sx={{ py: 1, px: 2 }}>
                                <Iconify icon="mdi:file-delimited-outline" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                <ListItemText primary="Export CSV" primaryTypographyProps={{ fontSize: '13px' }} />
                            </ListItemButton>
                        </List>
                    </Popover>
                </Box>
            </Box>

            <DataScopeIndicator variant="treatment" />

            {/* DataGrid / Graph */}
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {view === 'graph' ? (
                        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                            <MetricLineChart
                                type="bar"
                                categories={chartCategories}
                                series={chartSeries}
                                height={380}
                                stacked={viewMode === 'cohort'}
                                yAxisTitle="Leads"
                            />
                        </Box>
                    ) : (
                        <Box>
                            <DataGridPro
                                rows={filteredData}
                                columns={columns}
                                sortModel={sortModel}
                                onSortModelChange={setSortModel}
                                autoHeight
                                disableRowSelectionOnClick
                                disableColumnMenu={false}
                                pagination
                                pageSizeOptions={[5, 10, 25]}
                                initialState={{
                                    pagination: { paginationModel: { pageSize: 10 } },
                                }}
                                getRowId={(row) => row.treatmentId || row.id}
                                columnVisibilityModel={columnVisibilityModel}
                                sx={{
                                    border: 'none',
                                    '& .MuiDataGrid-columnHeaders': {
                                        bgcolor: '#f8fafc',
                                        borderBottom: '1px solid #e2e8f0',
                                    },
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        fontWeight: 600,
                                        fontSize: '12px',
                                        color: '#64748b',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                    },
                                    '& .MuiDataGrid-cell': {
                                        borderBottom: '1px solid #f1f5f9',
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        bgcolor: '#f8fafc',
                                    },
                                }}
                            />
                        </Box>
                    )}
                </>
            )}

            {filteredData.length === 0 && !isLoading && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: '#64748b' }}>
                        No treatment data available
                    </Typography>
                </Box>
            )}

            {/* Enquiry requests already include server treatment filtering for archived parents. */}
            <TreatmentLeadPopup
                open={popupOpen}
                onClose={handlePopupClose}
                practiceId={practiceIds?.[0] ?? practiceId}
                practiceIds={practiceIds}
                treatmentId={popupData.treatmentId}
                treatmentItemId={popupData.treatmentItemId}
                treatmentName={popupData.treatmentName}
                status={popupData.status}
                trustServerFilter={popupData.status === 'NEW'}
                viewMode={viewMode}
                dateRange={dateRange ? {
                    startDate: moment(dateRange.startDate).format('YYYY-MM-DD'),
                    endDate: moment(dateRange.endDate).format('YYYY-MM-DD'),
                } : null}
            />
        </Box>
    );
}

TreatmentStatusSummaryTable.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    practiceIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    dateRange: PropTypes.shape({
        startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
        endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    }),
    treatments: PropTypes.array,
    filterTreatmentIds: PropTypes.instanceOf(Set),
};

TreatmentStatusSummaryTable.defaultProps = {
    practiceId: null,
    practiceIds: null,
    dateRange: null,
    treatments: [],
    filterTreatmentIds: null,
};
